import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppSidebar from '@shared/components/AppSidebar';
import { clearAll } from '@/helpers/auth';
import { Permission } from '@/helpers/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import PhoenixLogo from '@/assets/images/phoenix-logo.png';
import { ReactComponent as DashboardIcon } from '@/assets/images/nav-dashboard.svg';
import { ReactComponent as ScheduleIcon } from '@/assets/images/nav-schedule.svg';
import { ReactComponent as SplitTripsIcon } from '@/assets/images/nav-split-trips.svg';
import { ReactComponent as AviationIcon } from '@/assets/images/nav-aviation.svg';
import { ReactComponent as SignOutIcon } from '@/assets/images/nav-sign-out.svg';

const APP_LOGO = { src: PhoenixLogo, alt: 'Phoenix Logo' };

const Sidebar = ({ children, isMobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userCan, userCanAny } = usePermissions();

  useEffect(() => {
    if (!userCan(Permission.VIEW_LEGS)) {
      navigate(`/`);
      clearAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(() => {
    const maverickUrl = (path) => `${process.env.REACT_APP_FE_MAVERICK}${path}`;
    const aviationLinks = [
      userCan(Permission.VIEW_AIRPORT) && { key: 'airports', label: 'Airports', href: maverickUrl('airports-list') },
      userCan(Permission.VIEW_AIRCRAFT) && { key: 'aircraft', label: 'Aircraft', href: maverickUrl('aircrafts-list') },
      userCan(Permission.VIEW_OPERATOR) && { key: 'operators', label: 'Operators', href: maverickUrl('operators-list') },
      userCan(Permission.VIEW_FBO) && { key: 'fbo', label: 'FBO', href: maverickUrl('fbo-list') },
    ].filter(Boolean);

    return [
      { key: 'dashboard', label: 'Dashboard', icon: DashboardIcon, to: '/', active: pathname === '/' },
      {
        key: 'schedule',
        label: 'Schedule',
        icon: ScheduleIcon,
        to: '/leg-list',
        active: pathname === '/leg-list' || pathname.includes('/scenario-builder') || pathname.includes('/send-to-broker'),
      },
      { key: 'split-trips', label: 'Split Trips', icon: SplitTripsIcon, to: '/split-groups', active: pathname === '/split-groups' },
      userCanAny([ Permission.VIEW_AIRPORT, Permission.VIEW_AIRCRAFT, Permission.VIEW_OPERATOR ]) && { key: 'aviation', label: 'Aviation', icon: AviationIcon, children: aviationLinks },
      {
        key: 'logout',
        label: 'Logout',
        icon: SignOutIcon,
        mobileOnly: true,
        onClick: () => {
          clearAll();
          navigate(`/login?return_url=${encodeURIComponent(window.location.pathname)}`);
        },
      },
    ].filter(Boolean);
  }, [ pathname, navigate, userCan, userCanAny ]);

  return (
    <AppSidebar
      appLogo={APP_LOGO}
      options={options}
      isMobileOpen={isMobileOpen}
      onMobileClose={onMobileClose}
      variant={process.env.REACT_APP_DEPLOY_ENV === 'prod' ? 'default' : 'nonProd'}
    >
      {children}
    </AppSidebar>
  );
};

export default Sidebar;
